import React from 'react';
import { 
  SellerStepProps, 
  getIndustryOptions,
  getCanadaProvinces,
  getUSStates,
  getEuropeCountries,
  getAustraliaStates,
} from '@/types/seller-registration.types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AddressAutocomplete } from '@/components/broker-registration/AddressAutocomplete';

export const SellerStepTwo: React.FC<SellerStepProps> = ({
  formData,
  updateFormData,
  errors,
  clearError,
  t,
}) => {
  const industryOptions = getIndustryOptions(t);
  const canadaProvinces = getCanadaProvinces(t);
  const usStates = getUSStates(t);
  const europeCountries = getEuropeCountries(t);
  const australiaStates = getAustraliaStates(t);

  const toggleJurisdiction = (region: 'jurisdictionsCanada' | 'jurisdictionsUSA' | 'jurisdictionsEurope' | 'jurisdictionsAustralia', value: string) => {
    const current = formData[region];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateFormData({ [region]: updated });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-[#1D314D] mb-2">
          {t.steps.name} 2: {t.step2.sectionTitle}
        </h3>
      </div>

      <div className="space-y-4">
        {/* Company Name */}
        <div className="space-y-2">
          <Label htmlFor="companyName" className="text-[#1D314D] font-semibold">
            {t.step2.companyName}
          </Label>
          <Input
            id="companyName"
            type="text"
            placeholder={t.step2.companyNamePlaceholder}
            value={formData.companyName}
            onChange={(e) => {
              updateFormData({ companyName: e.target.value });
              clearError('companyName');
            }}
            className="bg-white text-black border-gray-300"
          />
          {errors.companyName && <p className="text-red-400 text-sm">{errors.companyName}</p>}
        </div>

        {/* Industry */}
        <div className="space-y-2">
          <Label htmlFor="industry" className="text-[#1D314D] font-semibold">
            {t.step2.industry}
          </Label>
          <Select
            value={formData.industry}
            onValueChange={(value) => {
              updateFormData({ industry: value });
              clearError('industry');
            }}
          >
            <SelectTrigger className="bg-white text-black border-gray-300">
              <SelectValue placeholder={t.step2.industryPlaceholder} />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {industryOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.industry && <p className="text-red-400 text-sm">{errors.industry}</p>}
        </div>

        {/* Year Established */}
        <div className="space-y-2">
          <Label htmlFor="yearEstablished" className="text-[#1D314D] font-semibold">
            {t.step2.yearEstablished}
          </Label>
          <Input
            id="yearEstablished"
            type="number"
            placeholder={t.step2.yearEstablishedPlaceholder}
            min="1900"
            max="2022"
            value={formData.yearEstablished}
            onChange={(e) => {
              updateFormData({ yearEstablished: e.target.value });
              clearError('yearEstablished');
            }}
            className="bg-white text-black border-gray-300"
          />
          <p className="text-xs text-gray-400">{t.step2.yearEstablishedHint}</p>
          {errors.yearEstablished && <p className="text-red-400 text-sm">{errors.yearEstablished}</p>}
        </div>

        {/* Jurisdictions */}
        <div className="border border-[#CEAB65] p-4 rounded-lg bg-[#2a3f5f] space-y-4">
          <h4 className="text-lg font-bold text-[#CEAB65]">
            {t.step2.jurisdictionsTitle}
          </h4>
          <p className="text-xs text-gray-400">
            {t.step2.jurisdictionsHint}
          </p>

          {/* Canada */}
          <div className="space-y-2">
            <Label className="text-white font-semibold">{t.step2.jurisdictionsCanada}</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-2 bg-gray-700/30 rounded border border-gray-600">
              {canadaProvinces.map((prov) => (
                <label key={prov.value} className="flex items-center gap-2 text-sm text-gray-300">
                  <Checkbox
                    checked={formData.jurisdictionsCanada.includes(prov.value)}
                    onCheckedChange={() => toggleJurisdiction('jurisdictionsCanada', prov.value)}
                  />
                  {prov.label}
                </label>
              ))}
            </div>
          </div>

          {/* USA */}
          <div className="space-y-2">
            <Label className="text-white font-semibold">{t.step2.jurisdictionsUSA}</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-2 bg-gray-700/30 rounded border border-gray-600">
              {usStates.map((state) => (
                <label key={state.value} className="flex items-center gap-2 text-sm text-gray-300">
                  <Checkbox
                    checked={formData.jurisdictionsUSA.includes(state.value)}
                    onCheckedChange={() => toggleJurisdiction('jurisdictionsUSA', state.value)}
                  />
                  {state.label}
                </label>
              ))}
            </div>
          </div>

          {/* Europe */}
          <div className="space-y-2">
            <Label className="text-white font-semibold">{t.step2.jurisdictionsEurope}</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-2 bg-gray-700/30 rounded border border-gray-600">
              {europeCountries.map((country) => (
                <label key={country.value} className="flex items-center gap-2 text-sm text-gray-300">
                  <Checkbox
                    checked={formData.jurisdictionsEurope.includes(country.value)}
                    onCheckedChange={() => toggleJurisdiction('jurisdictionsEurope', country.value)}
                  />
                  {country.label}
                </label>
              ))}
            </div>
          </div>

          {/* Australia */}
          <div className="space-y-2">
            <Label className="text-white font-semibold">{t.step2.jurisdictionsAustralia}</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-2 bg-gray-700/30 rounded border border-gray-600">
              {australiaStates.map((state) => (
                <label key={state.value} className="flex items-center gap-2 text-sm text-gray-300">
                  <Checkbox
                    checked={formData.jurisdictionsAustralia.includes(state.value)}
                    onCheckedChange={() => toggleJurisdiction('jurisdictionsAustralia', state.value)}
                  />
                  {state.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Company Address - Merged with Google Maps */}
        <div className="space-y-2">
          <Label htmlFor="companyStreet" className="text-[#1D314D] font-semibold">
            {t.step2.companyStreet}
          </Label>
          <AddressAutocomplete
            value={formData.companyStreet || ''}
            onChange={(value) => {
              updateFormData({ companyStreet: value });
              clearError('companyStreet');
            }}
            onAddressSelect={(components) => {
              updateFormData({
                companyStreet: components.streetAddress,
                companyCity: components.city,
                companyProvince: components.stateProvince,
                companyZipCode: components.zipPostalCode,
              });
              clearError('companyStreet');
              clearError('companyCity');
              clearError('companyProvince');
              clearError('companyZipCode');
            }}
            placeholder={t.step2.companyStreetPlaceholder}
          />
          <p className="text-xs text-gray-400">{t.step2.addressSearchHint}</p>
          {errors.companyStreet && <p className="text-red-400 text-sm">{errors.companyStreet}</p>}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-2 col-span-2 md:col-span-1">
            <Label htmlFor="companyCity" className="text-[#1D314D] font-semibold">
              {t.step2.companyCity}
            </Label>
            <Input
              id="companyCity"
              type="text"
              placeholder={t.step2.companyCityPlaceholder}
              value={formData.companyCity}
              onChange={(e) => {
                updateFormData({ companyCity: e.target.value });
                clearError('companyCity');
              }}
              className="bg-white text-black border-gray-300"
            />
            {errors.companyCity && <p className="text-red-400 text-sm">{errors.companyCity}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyProvince" className="text-[#1D314D] font-semibold">
              {t.step2.companyProvince}
            </Label>
            <Input
              id="companyProvince"
              type="text"
              placeholder={t.step2.companyProvincePlaceholder}
              value={formData.companyProvince}
              onChange={(e) => {
                updateFormData({ companyProvince: e.target.value });
                clearError('companyProvince');
              }}
              className="bg-white text-black border-gray-300"
            />
            {errors.companyProvince && <p className="text-red-400 text-sm">{errors.companyProvince}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyZipCode" className="text-[#1D314D] font-semibold">
              {t.step2.companyZipCode}
            </Label>
            <Input
              id="companyZipCode"
              type="text"
              placeholder={t.step2.companyZipCodePlaceholder}
              value={formData.companyZipCode}
              onChange={(e) => {
                updateFormData({ companyZipCode: e.target.value });
                clearError('companyZipCode');
              }}
              className="bg-white text-black border-gray-300"
            />
            {errors.companyZipCode && <p className="text-red-400 text-sm">{errors.companyZipCode}</p>}
          </div>
        </div>


        {/* Pre-admission criteria */}
        <div className="border border-[#CEAB65] p-4 rounded-lg space-y-4">
          <h4 className="text-lg font-bold text-[#CEAB65]">{t.step2.documentsSection}</h4>

          {/* Positive Cash Flow */}
          <div className="space-y-2">
            <Label className="text-[#1D314D] font-semibold">
              {t.step2.positiveCashFlow}
            </Label>
            <RadioGroup
              value={formData.positiveCashFlow}
              onValueChange={(value) => {
                updateFormData({ positiveCashFlow: value as 'yes' | 'no' });
                clearError('positiveCashFlow');
              }}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="cashFlowYes" />
                <Label htmlFor="cashFlowYes" className="text-[#1D314D]">{t.step2.positiveCashFlowYes}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="cashFlowNo" />
                <Label htmlFor="cashFlowNo" className="text-[#1D314D]">{t.step2.positiveCashFlowNo}</Label>
              </div>
            </RadioGroup>
            {errors.positiveCashFlow && <p className="text-red-400 text-sm">{errors.positiveCashFlow}</p>}
            {formData.positiveCashFlow === 'no' && (
              <div className="bg-red-50 border border-red-300 rounded-lg p-3 mt-2">
                <p className="text-red-700 text-sm font-medium">{t.errors.positiveCashFlowNo}</p>
              </div>
            )}
          </div>

          {/* Required Documents */}
          <div className="space-y-2">
            <Label className="text-[#1D314D] font-semibold">{t.step2.documentsSection}</Label>
            <p className="text-xs text-gray-400 mb-2">{t.step2.documentsHint}</p>
            
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[#1D314D]">
                <Checkbox
                  checked={formData.doc3YearsPL}
                  onCheckedChange={(checked) => {
                    updateFormData({ doc3YearsPL: !!checked });
                    clearError('documents');
                  }}
                />
                {t.step2.doc3YearsPL}
              </label>
              <label className="flex items-center gap-2 text-[#1D314D]">
                <Checkbox
                  checked={formData.doc3YearsTax}
                  onCheckedChange={(checked) => {
                    updateFormData({ doc3YearsTax: !!checked });
                    clearError('documents');
                  }}
                />
                {t.step2.doc3YearsTax}
              </label>
              <label className="flex items-center gap-2 text-[#1D314D]">
                <Checkbox
                  checked={formData.docRecentBalance}
                  onCheckedChange={(checked) => {
                    updateFormData({ docRecentBalance: !!checked });
                    clearError('documents');
                  }}
                />
                {t.step2.docRecentBalance}
              </label>
            </div>
            {errors.documents && <p className="text-red-400 text-sm">{errors.documents}</p>}
          </div>

          {/* Documents Ready */}
          <div className="space-y-2">
            <Label htmlFor="documentsReady" className="text-[#1D314D] font-semibold">
              {t.step2.documentsReady}
            </Label>
            <Select
              value={formData.documentsReady}
              onValueChange={(value) => {
                updateFormData({ documentsReady: value as 'Yes' | 'No' | 'NeedHelp' });
                clearError('documentsReady');
              }}
            >
              <SelectTrigger className="bg-white text-black border-gray-300">
                <SelectValue placeholder="— Select —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">{t.step2.documentsReadyYes}</SelectItem>
                <SelectItem value="No">{t.step2.documentsReadyNo}</SelectItem>
              </SelectContent>
            </Select>
            {errors.documentsReady && <p className="text-red-400 text-sm">{errors.documentsReady}</p>}
          </div>
        </div>

        {/* First Sale */}
        <div className="space-y-2">
          <Label htmlFor="firstSale" className="text-[#1D314D] font-semibold">
            {t.step2.firstSale}
          </Label>
          <Select
            value={formData.firstSale}
            onValueChange={(value) => {
              updateFormData({ firstSale: value as 'Yes' | 'No' });
              clearError('firstSale');
            }}
          >
            <SelectTrigger className="bg-white text-black border-gray-300">
              <SelectValue placeholder="— Select —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">{t.step2.firstSaleYes}</SelectItem>
              <SelectItem value="No">{t.step2.firstSaleNo}</SelectItem>
            </SelectContent>
          </Select>
          {errors.firstSale && <p className="text-red-400 text-sm">{errors.firstSale}</p>}
        </div>

        {/* Need Guidance */}
        <div className="space-y-2">
          <Label htmlFor="needGuidance" className="text-[#1D314D] font-semibold">
            {t.step2.needGuidance}
          </Label>
          <Select
            value={formData.needGuidance}
            onValueChange={(value) => {
              updateFormData({ needGuidance: value as 'Yes' | 'No' });
              clearError('needGuidance');
            }}
          >
            <SelectTrigger className="bg-white text-black border-gray-300">
              <SelectValue placeholder="— Select —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">{t.step2.needGuidanceYes}</SelectItem>
              <SelectItem value="No">{t.step2.needGuidanceNo}</SelectItem>
            </SelectContent>
          </Select>
          {errors.needGuidance && <p className="text-red-400 text-sm">{errors.needGuidance}</p>}
        </div>
      </div>
    </div>
  );
};
